import React, { useState, useEffect, useRef } from "react";
import indBg from './assets/ind_bg.jpeg';
import swotLogo from './assets/SWOT_logo.jpeg';

// ─── DATA ────────────────────────────────────────────────────────────────────

const NAV = [
  { id: "home", label: "Home" },
  { id: "about", label: "About", sub: [
    { id: "about-who", label: "Who We Are" },
    { id: "about-vision", label: "Vision, Mission & Values" },
    { id: "about-why", label: "Why SWOT" },
    { id: "about-quality", label: "Quality & Integrity" },
  ]},
  { id: "services", label: "Services", sub: [
    { id: "svc-crossborder", label: "Cross-Border Tax & Structuring" },
    { id: "svc-fema", label: "FEMA, RBI & Regulatory" },
    { id: "svc-corporate", label: "Corporate Compliance" },
    { id: "svc-vcfo", label: "Virtual CFO & Accounting" },
    { id: "svc-tax", label: "Direct & Indirect Tax" },
    { id: "svc-transaction", label: "Transaction & M&A" },
    { id: "svc-valuation", label: "Valuation & ESOP" },
    { id: "svc-family", label: "Family Office & Succession" },
    { id: "svc-payroll", label: "Payroll, HR & POSH" },
    { id: "svc-setup", label: "Market Entry & Business Set-up" },
    { id: "svc-banking", label: "Banking & Treasury" },
    { id: "svc-process", label: "Process, ERP & Transformation" },
    { id: "svc-regulatory", label: "Regulatory Representation" },
    { id: "svc-additional", label: "Additional Services" },
  ]},
  { id: "industries", label: "Industries" },
  { id: "engagement", label: "Engagement Model" },
  { id: "insights", label: "Insights" },
  { id: "careers", label: "Careers" },
  { id: "contact", label: "Contact" },
];

const SERVICES_OVERVIEW = [
  { id: "svc-crossborder", icon: "🌐", title: "Cross-Border Tax & Structuring", desc: "Inbound/outbound structuring, treaty analysis, PE/POEM/SEP review, transfer pricing, withholding, repatriation and cross-border transaction planning." },
  { id: "svc-fema", icon: "🏛", title: "FEMA, RBI & Regulatory Advisory", desc: "FDI, ODI, ECB, RBI filings, AD-bank coordination, compounding, approvals/clarifications and sectoral regulatory support." },
  { id: "svc-corporate", icon: "📋", title: "Corporate Compliance & Secretarial Support", desc: "Companies Act, LLP, ROC, board/shareholder documentation, statutory registers, director compliance and governance support." },
  { id: "svc-vcfo", icon: "📊", title: "Virtual CFO, Accounting & MIS", desc: "Outsourced finance leadership, month-end close, MIS, Capex - project portfolio management support, budgets, forecasts, dashboards, cash-flow planning, Financial Planning and Analysis (FP&A) and Reporting, Business intelligence (BI-BO) – Dashboards, accounting and consolidation support." },
  { id: "svc-tax", icon: "📑", title: "Direct & Indirect Tax Support", desc: "Income tax, GST, withholding tax, transfer pricing, international tax, assessments, appeals and advisory support through eligible professionals wherever required." },
  { id: "svc-transaction", icon: "🤝", title: "Transaction, M&A & Restructuring Advisory", desc: "Buy-side/sell-side support, financial/tax/regulatory due diligence, SPA inputs, closing mechanics, post-merger integration and restructuring support." },
  { id: "svc-valuation", icon: "📈", title: "Valuation, ESOP & Financial Modelling", desc: "Business valuation support, share/security valuation, ESOP valuation, financial models, investor decks, PPA support and fairness-opinion coordination where applicable." },
  { id: "svc-family", icon: "🏠", title: "Family Office & Succession Advisory", desc: "Holding structures, family governance, estate-planning coordination, trust/will coordination, HNI reporting and succession documentation support." },
  { id: "svc-payroll", icon: "👥", title: "Payroll, HR, POSH, CSR & Labour Compliance", desc: "Payroll processing, statutory contributions, employee reimbursements, POSH framework support, CSR compliance support and labour-compliance coordination." },
  { id: "svc-setup", icon: "🚀", title: "Business Set-up & Establishment", desc: "Company/LLP incorporation, foreign office set-up, tax registrations, banking coordination, office establishment support and ongoing compliance alignment." },
  { id: "svc-banking", icon: "🏦", title: "Banking, Funding & Treasury Support", desc: "Project reports, lender information packs, working capital analysis, cash-flow forecasting, treasury dashboards and funding discussion support, subject to applicable regulations." },
  { id: "svc-process", icon: "⚙️", title: "Process, Controls, ERP & Transformation", desc: "SOPs, internal control design support, process re-engineering, ERP selection/implementation oversight, finance automation and compliance trackers." },
  { id: "svc-regulatory", icon: "⚖️", title: "Regulatory Representation & Documentation Support", desc: "Documentation, replies, applications and representation support before tax, RBI, MCA, ROC and other authorities." },
  { id: "svc-additional", icon: "⚖️", title: "Additional Services", desc: "SWOT provides business, management and support services beyond traditional compliance work, covering business consulting, investor readiness, banking documentation, ERP support, managed compliance, research, training and back-office process support. " },
];

const SERVICE_DETAILS = {
  "svc-crossborder": {
    title: "Cross-Border Tax and Structuring",
    intro: "SWOT supports clients on inbound, outbound and multi-jurisdictional tax and structuring matters. The work is designed for businesses, investors, family offices and multinational groups that operate across borders and need coordinated tax, regulatory and commercial analysis.",
    items: [
      ["Inbound investment structuring", "Entity selection, jurisdictional analysis, treaty access review, FDI route review and India entry-structure support."],
      ["Outbound investment structuring", "Holding company analysis, intermediate jurisdictions, ODI compliance, substance planning and overseas expansion support."],
      ["DTAA and treaty analysis", "Treaty relief analysis, limitation-of-benefit review, Most-Favoured-Nation clause assessment and withholding implications."],
      ["PE, POEM and SEP review", "Permanent Establishment risk review, Place of Effective Management analysis and Significant Economic Presence assessment."],
      ["Transfer pricing", "Documentation, benchmarking, country-by-country reporting support, master file / local file coordination and APA support."],
      ["BEPS, GAAR and MLI", "Impact analysis of BEPS principles, GAAR considerations and Multilateral Instrument implications on existing or proposed structures."],
      ["Withholding tax", "Domestic and international withholding-tax review, Form 15CA/15CB coordination, grossing-up analysis and cross-border payment support."],
      ["Repatriation strategy", "Planning for dividends, royalties, interest, service fees, capital repatriation and exit structures across treaty networks."],
      ["Cross-border documentation", "Inter-company agreements, board notes, substance documentation and transaction support papers in coordination with legal counsel where required."],
    ]
  },
  "svc-fema": {
    title: "FEMA, RBI and Regulatory Advisory",
    intro: "SWOT assists with foreign-exchange, banking and sectoral regulatory matters. These services are particularly relevant for foreign investors entering India, Indian businesses investing overseas and companies managing cross-border capital, debt, royalty, service-fee or operational arrangements.",
    items: [
      ["FEMA advisory", "Advisory support across capital account and current account transactions under FEMA and related rules/regulations."],
      ["RBI representation and coordination", "AD-bank coordination, RBI application support, clarification support, compounding applications and regulatory correspondence."],
      ["FDI filings", "FC-GPR, FC-TRS, LLP-(I), LLP-(II), downstream investment reporting and related documentation."],
      ["ODI support", "Form FC, Annual Performance Reports, restructuring of overseas entities, disinvestment and reporting support."],
      ["ECB support", "ECB structuring, registration support, ECB-2 reporting, end-use monitoring and compliance documentation."],
      ["Foreign offices in India", "Liaison Office, Branch Office and Project Office set-up, approvals, AAC filing support, operational compliance and closure."],
      ["GIFT City / IFSCA", "Regulatory pathway support for entities evaluating IFSCA / GIFT City structures, subject to applicable registrations."],
      ["Sectoral regulatory support", "SEBI, IRDAI, telecom, media and other sectoral regulatory support in coordination with eligible professionals where required."],
      ["PMLA, KYC and beneficial ownership", "Policy documentation, beneficial ownership reporting support, KYC checklists and AML documentation assistance."],
    ]
  },
  "svc-corporate": {
    title: "Corporate Compliance and Secretarial Support",
    intro: "SWOT supports companies and LLPs in corporate law, governance documentation and recurring secretarial compliance. Where the law requires a Company Secretary in practice or other eligible professional, the work should be routed through or reviewed by such eligible professional.",
    items: [
      ["Companies Act compliance", "Board, committee and shareholder governance documentation, statutory registers and compliance calendars."],
      ["Board and shareholder documentation", "Drafting and maintenance support for notices, agendas, minutes, board resolutions, shareholder resolutions and corporate policies."],
      ["ROC / MCA filings", "Annual returns, financial statements, event-based filings, charge management and form-preparation support."],
      ["Corporate actions", "Changes in directors, registered office, share capital, securities issue, charges, name changes and other corporate events."],
      ["Conversions", "Private to public, LLP to company and similar conversions with ROC, tax and regulatory coordination."],
      ["Mergers, demergers and schemes", "Documentation, process coordination, filing support and compliance planning for corporate restructuring."],
      ["SEBI / LODR support", "Listed-company compliance support and secretarial-compliance assistance where applicable, through eligible professionals."],
      ["Director-level compliance", "DIN, DSC, KYC, declarations, disclosures of interest and ongoing director compliance support."],
      ["Governance advisory", "Governance frameworks, delegation matrices, policy documents, group-compliance calendars and entity-management support."],
    ]
  },
  "svc-vcfo": {
    title: "Virtual CFO, Accounting and Management Reporting",
    intro: "SWOT provides outsourced finance and accounting support for businesses that need disciplined reporting, cash-flow visibility, budget control and management information without building a full in-house finance leadership function immediately.",
    items: [
      ["Outsourced finance function", "Month-end close, period reporting, accounting supervision and finance-process ownership."],
      ["Accounting and bookkeeping", "Full-cycle bookkeeping aligned with Ind AS, IFRS, Singapore FRS or other applicable frameworks, based on agreed scope."],
      ["MIS and dashboards", "Monthly MIS, management packs, KPI dashboards, board reporting and variance analysis."],
      ["Budgeting and forecasting", "Annual budgets, rolling forecasts, scenario models, FP&A and Business Intelligence dashboards."],
      ["Cash flow and working capital", "Cash-flow tracking, receivable/payable reporting, working-capital optimisation and treasury support."],
      ["Investor reporting", "Reporting packs for venture-backed, PE-backed or lender-monitored businesses."],
      ["Consolidation support", "Group schedules, consolidation packs, inter-company reconciliations and reporting support across entities."],
      ["Finance technology", "ERP/accounting software selection support, implementation oversight, chart-of-accounts design and reporting automation."],
      ["Strategic finance inputs", "Pricing, unit economics, gross-margin review, capital structure and business-model analysis."],
    ]
  },
  "svc-tax": {
    title: "Direct and Indirect Tax Support",
    intro: "SWOT supports clients in direct tax, GST, withholding, international tax and transfer pricing matters, through eligible professionals where required.",
    items: [
      ["Corporate tax compliance", "Income-tax return preparation support, tax-computation review, advance-tax planning and compliance calendar support."],
      ["Assessments and replies", "Preparation of replies, reconciliations, document sets and submissions for assessment proceedings."],
      ["Appeals and litigation support", "Preparation of statements of facts, grounds of appeal, written submissions and support to authorised representatives/counsel."],
      ["International tax", "Tax residency, treaty positions, cross-border payments, withholding, PE exposure and documentation."],
      ["Transfer pricing", "Benchmarking, documentation, Form 3CEB coordination, APA support and related advisory."],
      ["Withholding tax", "TDS/TCS advisory, lower-deduction applications, domestic and foreign remittance support and compliance review."],
      ["Expatriate tax", "Residential status, payroll tax, assignment structuring and annual compliance support for internationally mobile employees."],
      ["GST registration and amendments", "GST registration, amendment and closure support based on business requirements."],
      ["Return and reconciliation support", "GSTR filings, input-tax credit review, GSTR-2B vs books reconciliation, e-invoicing and e-way bill compliance support."],
      ["GST advisory", "Classification, valuation, place of supply, reverse charge, refunds, inverted duty structure, exports and credit eligibility."],
      ["GST assessments, audit and appeals", "Preparation of replies, document sets, reconciliations and representation support through eligible professionals."],
      ["Other indirect taxes", "Legacy indirect-tax matters, customs support and sector-specific indirect-tax review in coordination with specialists where required."],
    ]
  },
  "svc-transaction": {
    title: "Transaction, M&A and Restructuring Advisory",
    intro: "SWOT supports businesses, investors and family offices in transaction planning, diligence, structuring and implementation support. The company can provide commercial and financial advisory while coordinating legal drafting, regulated valuation or merchant-banking functions through appropriate specialists where required.",
    items: [
      ["Buy-side advisory support", "Financial, tax, regulatory and accounting inputs for acquisition evaluation and negotiation support."],
      ["Sell-side advisory support", "Data-room preparation, management information review, vendor diligence coordination and response support."],
      ["Due diligence", "Financial, tax, secretarial, regulatory, payroll and operational diligence support."],
      ["SPA and transaction documentation inputs", "Commercial and financial inputs into sale-purchase agreements, disclosure schedules, closing mechanics and conditions precedent."],
      ["Mergers and restructuring", "Schemes of arrangement, demergers, slump sales, share swaps, holding company creation and group simplification support."],
      ["Post-merger integration", "Finance process integration, reporting alignment, compliance calendar integration and control handover support."],
      ["Data room and Q&A", "Document indexing, management responses, information packs and Q&A coordination."],
    ]
  },
  "svc-valuation": {
    title: "Valuation, ESOP and Financial Modelling Support",
    intro: "SWOT assists in valuation, financial modelling and ESOP-related assignments for management decision-making, regulatory evaluation, transaction planning and reporting support. Where a valuation must be issued by a Registered Valuer, Merchant Banker or other eligible person, SWOT shall coordinate through the appropriate registered professional.",
    items: [
      ["Business valuation support", "Financial analysis, DCF models, comparable analysis, NAV analysis and valuation documentation support."],
      ["Share and security valuation", "Valuation support for equity shares, preference shares, debentures, convertible instruments and other securities."],
      ["Income-tax / FEMA valuation coordination", "Preparation of data, models and documentation for valuation reports required under tax or FEMA frameworks."],
      ["ESOP support", "ESOP pool analysis, perquisite-value support, accounting inputs, plan documentation support and valuation coordination."],
      ["Financial modelling", "Integrated P&L, balance sheet, cash flow, unit economics, scenario models and investor/lender models."],
      ["Fairness opinions / PPA coordination", "Coordination support for fairness opinions and purchase price allocation where applicable through eligible professionals."],
      ["Investor decks", "Financial story, operating metrics, use of funds, cap table analysis and data-room financial schedules."],
    ]
  },
  "svc-family": {
    title: "Family Office, Succession and HNI Advisory",
    intro: "SWOT assists family offices, promoters and HNI families with financial, tax, entity-management and governance support.",
    items: [
      ["Family office reporting", "Consolidated reporting packs, asset/liability schedules, investment records and family-office dashboards."],
      ["Holding structure design", "Tax and regulatory analysis for family holdings, investment companies, LLPs, trusts and international structures."],
      ["Succession coordination", "Coordination with legal counsel on wills, trusts, family arrangements and generational transition planning."],
      ["Family governance", "Family charters, decision matrices, investment reporting protocols and documentation controls."],
      ["Estate and settlement support", "Financial, tax and documentation support in estate planning and family settlements."],
      ["Cross-border family wealth", "Remittance, FEMA, offshore entity, reporting and tax considerations for multi-jurisdictional families."],
    ]
  },
  "svc-payroll": {
    title: "Payroll, HR, POSH, CSR and Labour Compliance",
    intro: "SWOT assists businesses with payroll processing, HR documentation, statutory labour compliances, POSH implementation and CSR compliance support. The work includes documentation, filings, registers, policy support, reporting assistance and coordination with relevant professionals wherever required.",
    items: [
      ["Payroll processing", "Payroll computation, salary registers, reimbursements, employee master data, leave records and payroll reporting."],
      ["Statutory payroll compliance", "PF, ESI, professional tax, labour welfare fund and payroll tax support where applicable."],
      ["Employee benefits and reimbursements", "Payroll policies, reimbursement workflows, employee declarations and supporting documentation."],
      ["POSH compliance support", "POSH policy drafting support, Internal Committee constitution assistance, annual disclosure support and workplace compliance documentation."],
      ["CSR compliance support", "CSR policy drafting support, CSR committee documentation, statutory reporting, disclosure and record-maintenance assistance."],
      ["MSME / Udyam registration", "Documentation review, application filing, modification and updation support."],
      ["Start-up recognition", "Documentation preparation, filing and coordination support for start-up recognition under applicable government initiatives."],
      ["IEC registration", "Import Export Code registration, modification and updation support."],
      ["HR documentation", "Offer letter formats, employment record checklists, HR compliance calendars and employee documentation support."],
    ]
  },
  "svc-setup": {
    title: "Market Entry, Business Set-up and Establishment Support",
    intro: "SWOT assists domestic and international businesses with market-entry planning, entity set-up, registrations, banking coordination, regulatory filings and post-incorporation compliance support.",
    items: [
      ["India entry support", "Entity options, FDI route review, incorporation coordination, tax registrations and initial compliance calendar."],
      ["Singapore and international set-up support", "Company incorporation coordination, accounting set-up, bank-account coordination and compliance planning in selected jurisdictions."],
      ["Foreign office establishment in India", "Liaison office, branch office and project office establishment support, approvals and post-approval compliance."],
      ["Banking coordination", "Documentation packs, KYC support and coordination with banks for operating accounts and regulatory matters."],
      ["Tax and business registrations", "PAN, TAN, GST, IEC, professional tax, shops and establishment and other registrations as applicable."],
      ["Office set-up support", "Commercial setup checklists, vendor onboarding, accounting workflows, document controls and initial finance policies."],
      ["Ongoing compliance alignment", "Integrated compliance calendar covering corporate, tax, FEMA, payroll, labour and sectoral requirements."],
    ]
  },
  "svc-banking": {
    title: "Banking, Funding Support and Treasury Advisory",
    intro: "SWOT assists businesses in managing their banking relationships, funding documentation, treasury planning and working-capital requirements through structured financial and advisory support.",
    items: [
      ["Project reports", "Preparation of business plans, project reports, CMA-style data, assumptions and financial projections for internal or lender discussions."],
      ["Lender information packs", "Financial schedules, historical performance summaries, security/collateral information and response packs for bank/lender queries."],
      ["Working-capital analysis", "Receivable, payable, inventory, cash-conversion cycle and drawing-power analysis."],
      ["Cash-flow forecasting", "Short-term and long-term cash-flow models, repayment schedules, covenant tracking and liquidity dashboards."],
      ["Treasury support", "Banking limits tracking, fund-flow monitoring, forex exposure summaries and treasury reporting."],
      ["Funding discussion support", "Support for discussions with banks, NBFCs, investors or financial institutions. This should not be described as loan arrangement, securities placement or regulated investment banking unless undertaken through licensed intermediaries."],
      ["Investor / lender reporting", "Periodic reporting packs, covenant compliance support and information coordination."],
    ]
  },
  "svc-process": {
    title: "Process, Controls, ERP and Business Transformation",
    intro: "SWOT assists businesses in reviewing and improving finance, accounting and operational processes through SOPs, internal-control frameworks, ERP/process implementation support, automation, reporting dashboards and workflow redesign.",
    items: [
      ["SOP development and review", "Preparation, review and implementation support for SOPs across finance, purchase, sales, inventory, payroll and compliance functions."],
      ["Internal control design support", "Design and documentation of control matrices, authorisation limits, maker-checker workflows and exception reporting."],
      ["Audit readiness support", "Schedules, reconciliations, working papers, management responses and documentation support for audits conducted by independent auditors."],
      ["Business process re-engineering", "Review of process gaps, reporting delays, control weaknesses and workflow inefficiencies."],
      ["ERP selection and implementation oversight", "Requirement mapping, vendor coordination, chart-of-accounts design, UAT support and go-live readiness."],
      ["Finance automation", "Excel/Power BI dashboards, compliance trackers, reconciliation tools, reporting templates and workflow automation."],
      ["Data rooms and document controls", "Secure data-room structuring, document indexing, access controls and data-retention protocols."],
      ["ITGC and systems review support", "Review support for access controls, change management, posting-period controls, system-generated reports and finance-system governance, without giving audit assurance."],
    ]
  },
  "svc-regulatory": {
    title: "Regulatory Representation and Documentation Support",
    intro: "SWOT assists businesses with preparation of replies, submissions, applications, filings and supporting documentation for matters before tax, corporate, FEMA/RBI and other regulatory authorities.",
    items: [
      ["Tax authorities", "Drafting of replies, submissions, reconciliations and document sets for assessment, rectification, appeal and verification matters."],
      ["RBI and AD banks", "Applications, clarifications, compounding support, filing follow-up and regulatory-documentation support."],
      ["MCA / ROC / Regional Director", "Form filings, replies, compounding support, records preparation and representation support where permitted."],
      ["SEBI / stock exchange support", "Compliance documentation and response support where applicable, through eligible professionals."],
      ["NCLT / tribunal support", "Financial, tax and compliance documentation support to legal counsel and authorised representatives."],
      ["Labour and HR authorities", "Payroll, PF/ESI, labour compliance documentation and response coordination."],
      ["Government registrations", "MSME/Udyam, Start-up recognition, IEC and other business registrations or updates."],
    ]
  },
  "svc-additional": {
    title: "Additional Services",
    intro: "SWOT provides business, management and support services beyond traditional compliance work, covering business consulting, investor readiness, banking documentation, ERP support, managed compliance, research, training and back-office process support. These services are delivered as advisory, documentation, coordination and implementation-support assignments, subject to applicable laws and regulatory requirements.",
    items: [
      ["Business consulting and management advisory", "Strategic planning, operating model review, organisation design, KPI frameworks, growth dashboards and management reporting."],
      ["Fund-raising support / investor readiness", "Investor decks, data-room readiness, financial models, due-diligence responses and investor-reporting packs. Avoid regulated securities placement unless through licensed intermediaries."],
      ["Loan and banking documentation support", "Project reports, lender packs, financial projections and bank-query support. Avoid “loan arrangement” or commission-based regulated intermediation unless legally cleared."],
      ["Recruitment and finance-team support", "Finance-role JD preparation, interview support, onboarding checklists and finance function structuring."],
      ["ERP / software implementation support", "Accounting software selection, ERP implementation oversight, reporting automation and system-control documentation."],
      ["Business process outsourcing", "Accounting, payroll, compliance desk, vendor processing, receivable tracking and back-office finance operations."],
      ["Market-entry and establishment services", "Entity set-up, banking coordination, tax registrations, office set-up checklists and vendor onboarding support."],
      ["Corporate concierge / managed compliance desk", "Single-window compliance calendar, filing tracker, document repository and board/compliance reminder desk."],
      ["Business research and benchmarking", "Sector notes, comparable analysis, pricing analysis, competitor scan and decision-support research."],
      ["Training and workshops", "Finance, GST, FEMA, corporate compliance, internal controls, POSH, ERP and reporting-process training for client teams."],
      ["Brand-neutral website/communication content", "Technical notes, policy drafts, investor reporting content and board presentation support. Avoid public claims that create professional or legal exposure."],
    ]
  },
};

const INDUSTRIES = [
  { icon: "🏦", group: "Financial Services", sectors: "Banking, NBFCs, asset management, fintech, payment systems, AIFs and insurance intermediaries." },
  { icon: "💻", group: "Technology & Digital", sectors: "SaaS, software services, platform businesses, online marketplaces and emerging technology ventures." },
  { icon: "🏭", group: "Manufacturing & Engineering", sectors: "Industrial manufacturing, automotive components, capital goods, process industries and engineering services." },
  { icon: "🏗", group: "Real Estate & Infrastructure", sectors: "Construction, real estate development, REITs, InvITs, infrastructure operators and project finance vehicles." },
  { icon: "💊", group: "Pharmaceuticals & Healthcare", sectors: "Pharmaceutical formulations, APIs, hospitals, diagnostics, medical devices and life sciences." },
  { icon: "🛍", group: "Retail & Consumer", sectors: "Branded retail, FMCG, food and beverage, lifestyle, e-commerce and consumer durables." },
  { icon: "💼", group: "Professional Services", sectors: "Consulting firms, law firms, recruitment agencies, design studios and creative-industry businesses." },
  { icon: "📦", group: "Trading & Distribution", sectors: "Import-export, wholesale distribution, commodity trading, supply chain and logistics." },
  { icon: "⚡", group: "Energy & Resources", sectors: "Renewable energy, oil and gas, mining, metals and natural resources." },
  { icon: "🎬", group: "Media & Hospitality", sectors: "Print media, digital media, broadcast media, entertainment, travel, tourism, leisure and hotels." },
  { icon: "👨‍👩‍👧‍👦", group: "Family Offices & HNIs", sectors: "Wealth structuring, estate planning, succession advisory and family governance." },
];

const WHY_SWOT = [
  { title: "~30 Years of Continuity", desc: "Established in 1997, SWOT brings nearly three decades of unbroken professional continuity, institutional memory and disciplined practice management." },
  { title: "Qualified Professional Leadership", desc: "Engagements are led by a multidisciplinary team with deep functional expertise across finance, taxation, corporate law and regulatory practice." },
  { title: "Cross-Border Capability", desc: "Genuine, hands-on capability for inbound investment into India and outbound expansion from India — supported by treaty fluency and regulatory depth." },
  { title: "Integrated, Single-Accountability Model", desc: "A single firm covering tax, regulatory, secretarial, FEMA, Virtual CFO and transaction needs — eliminating coordination friction." },
  { title: "Boutique Attention. Institutional Discipline.", desc: "The personalised attention of an independent firm, delivered through institutional-grade quality controls, documentation standards and confidentiality protocols." },
  { title: "Unwavering Independence", desc: "An uncompromising commitment to professional independence and freedom from conflicts of interest — every engagement assessed formally." },
  { title: "Technology-Enabled Delivery", desc: "Cloud accounting platforms, secure data rooms, encrypted communications, and automated compliance trackers ensure accuracy and timeliness." },
  { title: "Fee Transparency", desc: "Clear, upfront engagement letters; defined scope; no surprise billing. Our clients know what they are paying for, and what they will receive." },
];

const ENGAGEMENT_MODELS = [
  { name: "Foundation", icon: "🌱", desc: "For start-ups, new incorporations and early-stage businesses: entity set-up coordination, foundational compliance, bookkeeping and tax filing support." },
  { name: "Growth", icon: "📈", desc: "For established SMEs: monthly accounting, tax/GST filings, periodic compliance review, management reporting and advisory consultations." },
  { name: "Virtual CFO", icon: "🎯", desc: "For scaling, venture-backed and PE-backed companies: financial strategy, budgeting, forecasting, investor reporting, treasury management and board-level engagement." },
  { name: "Cross-Border Advisory", icon: "🌍", desc: "For inbound, outbound and multi-jurisdictional clients: market-entry strategy, tax structuring, FEMA/RBI compliance and dual-jurisdiction set-up." },
  { name: "Bespoke Mandates", icon: "⭐", desc: "For complex transactions and special situations: M&A advisory, due diligence, regulatory representation, restructuring, valuation coordination and transformation projects." },
];

// ─── THEME GRADIENT (shared across all dark sections) ────────────────────────
// Matches the hero overlay: full-opacity version of the hero's 155deg gradient
const DARK_BG = `linear-gradient(155deg, #17305a 0%, #26446d 52%, #3b5a82 100%)`;

// ─── STYLES ──────────────────────────────────────────────────────────────────

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --navy: #0a1628;
    --navy-mid: #102040;
    --navy-light: #1a3055;
    --gold: #c9a84c;
    --gold-light: #e2c27a;
    --cream: #f8f4ee;
    --text: #1a1a2e;
    --muted: #6b7a8d;
    --border: #e2d9cc;
    --white: #ffffff;
    --sidebar-w: 270px;
    --header-h: 64px;

    /* Shared dark gradient — matches hero overlay at full opacity */
    --dark-gradient: linear-gradient(155deg, #17305a 0%, #26446d 52%, #3b5a82 100%);
    --dark-border: rgba(255,255,255,0.08);
  }

  html { scroll-behavior: smooth; }

  body {
    font-family: "Helvetica", Sans-serif;
    background: var(--cream);
    color: var(--text);
    line-height: 1.6;
    font-size: 16px;
  }

  h1,h2,h3,h4 {
    font-family: 'Cormorant Garamond', serif;
    line-height: 1.2;
    font-weight: 600;
  }

  /* ── HEADER ── */
  .header {
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 100;
    height: var(--header-h);
    /* Exactly the header sapphire tone */
    background: #173253;
    display: flex;
    align-items: center;
    padding: 0 24px;
    gap: 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    box-shadow: 0 10px 28px rgba(0,0,0,0.18), 0 1px 0 rgba(255,255,255,0.025) inset;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }
  .header-logo {
    display:flex; align-items:center; gap:10px; cursor:pointer; flex-shrink:0;
  }
  .header-logo-mark {
    width:90px;
    // background: linear-gradient(135deg, var(--gold), var(--gold-light));
    border-radius:6px;
    display:flex; align-items:center; justify-content:center;
    // font-family:'Cormorant Garamond',serif;
    // font-weight:700; font-size:18px; color:var(--navy);
    letter-spacing:-1px;
  }
  .header-logo-text {
    font-family:'Cormorant Garamond',serif;
    font-size:17px; font-weight:600; color:var(--cream); line-height:1.1;
  }
  .header-logo-sub {
    font-family:'DM Sans',sans-serif;
    font-size:10px; color:var(--gold); font-weight:400;
    letter-spacing:0.08em; text-transform:uppercase;
  }
  .header-tagline {
    font-size:11px; color:#8899aa; letter-spacing:0.06em;
    text-transform:uppercase; margin-left:auto; margin-right:16px;
    max-height: var(--header-h);
    overflow: hidden;
  }
  // @media(min-width:900px){ .header-tagline{ display:block; } }
  .header-contact-btn {
    background: var(--gold); color:var(--navy);
    border:none; padding:8px 18px; border-radius:4px;
    font-family:'DM Sans',sans-serif; font-size:13px; font-weight:600;
    cursor:pointer; white-space:nowrap; flex-shrink:0;
    transition: background 0.2s;
  }
  .header-contact-btn:hover { background:var(--gold-light); }
  .sidebar-toggle {
    background:none; border:none; color:var(--cream);
    cursor:pointer; padding:6px; display:flex; align-items:center; flex-shrink:0;
  }

  /* ── SIDEBAR ──
     Matches the header: #173253 base, same sapphire family */
  .sidebar-overlay {
    position:fixed; inset:0; background:rgba(0,0,0,0.55);
    z-index:150; opacity:0; pointer-events:none; transition: opacity 0.25s;
  }
  .sidebar-overlay.open { opacity:1; pointer-events:all; }

  .sidebar {
    position:fixed; top:0; left:0; bottom:0;
    width:var(--sidebar-w);
    /* Header-matching sapphire-navy — NOT the old pure --navy */
    background: #173253;
    z-index:200;
    transform: translateX(-100%);
    transition: transform 0.28s cubic-bezier(.4,0,.2,1);
    display:flex; flex-direction:column; overflow:hidden;
    /* Subtle right-edge separator like header bottom border */
    border-right: 1px solid rgba(255,255,255,0.06);
    box-shadow: 4px 0 24px rgba(0,0,0,0.22);
  }
  .sidebar.open { transform:translateX(0); }

  .sidebar-header {
    padding:20px 20px 16px;
    /* Match header exactly */
    background: #173253;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    display:flex; align-items:center; justify-content:space-between; flex-shrink:0;
  }
  .sidebar-close {
    background:none; border:none; color:#8899aa;
    cursor:pointer; font-size:20px; line-height:1;
    transition: color 0.15s;
  }
  .sidebar-close:hover { color:var(--cream); }

  .sidebar-scroll { overflow-y:auto; flex:1; padding:12px 0 24px; }

  .sidebar-nav-item {
    display:flex; width:100%; background:none; border:none;
    text-align:left; cursor:pointer; color:var(--cream);
    padding:10px 20px;
    font-family:'DM Sans',sans-serif; font-size:14px; font-weight:500;
    align-items:center; justify-content:space-between;
    transition: background 0.15s, color 0.15s;
    border-left: 3px solid transparent;
  }
  /* Hover: slightly lighter layer of the same sapphire */
  .sidebar-nav-item:hover { background: rgba(255,255,255,0.06); }
  .sidebar-nav-item.active { color:var(--gold); border-left-color:var(--gold); background:rgba(201,168,76,0.1); }
  .sidebar-nav-item .arrow { font-size:10px; color:#8899aa; transition: transform 0.2s; }
  .sidebar-nav-item.expanded .arrow { transform: rotate(90deg); }

  /* Sub-menu: one shade deeper within the same sapphire family */
  .sidebar-sub {
    overflow:hidden; transition: max-height 0.25s ease;
    background: rgba(0,0,0,0.18);
  }
  .sidebar-sub-item {
    display:flex; width:100%; background:none; border:none;
    text-align:left; cursor:pointer;
    padding:8px 20px 8px 36px;
    font-family:'DM Sans',sans-serif; font-size:13px; color:#aabbcc;
    transition: color 0.15s, background 0.15s;
    border-left: 3px solid transparent;
  }
  .sidebar-sub-item:hover { color:var(--cream); background:rgba(255,255,255,0.05); }
  .sidebar-sub-item.active { color:var(--gold); border-left-color:var(--gold); }
  .sidebar-divider { height:1px; background:rgba(255,255,255,0.08); margin:8px 20px; }

  /* ── MAIN LAYOUT ── */
  .page-wrap { padding-top: var(--header-h); min-height:100vh; }

  /* ── SECTION WRAPPER ── */
  .section { padding:64px 0; }
  .section:nth-child(even) { background:var(--white); }
  .container { max-width:1100px; margin:0 auto; padding:0 32px; }

  /* ── HERO ── */
  .hero {
    position: relative;
    color: var(--cream);
    padding: 100px 0 88px;
    overflow: hidden;
    min-height: 580px;
  }
  .hero-bg {
    position: absolute; inset: 0;
    background-size: cover; background-position: center 30%;
    transform: scale(1.03); z-index: 0;
  }
  .hero-overlay {
    position: absolute; inset: 0; z-index: 1;
    background: linear-gradient(
      155deg,
      rgba(23, 48, 90, 0.90) 0%,
      rgba(38, 68, 109, 0.90) 52%,
      rgba(59, 90, 130, 0.90) 100%
    );
  }
  .hero-content { position: relative; z-index: 2; }
  .hero::before {
    content: ''; position: absolute; top: -100px; right: -100px;
    width: 520px; height: 520px; border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.06);
    background: radial-gradient(circle, rgba(255,255,255,0.03) 0%, rgba(140,170,220,0.04) 32%, transparent 72%);
    pointer-events: none;
  }
  .hero::after {
    content: ''; position: absolute; top: 30px; right: -30px;
    width: 300px; height: 300px; border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.045);
    background: radial-gradient(circle, rgba(255,255,255,0.02) 0%, transparent 70%);
    pointer-events: none;
  }

  .header-btn {
    margin-left: 30px;
    display: flex;
    flex-shrink: 0;
  }
  .hero-badge-btn {
    background: rgba(201,168,76,0.14); border: 1px solid rgba(201,168,76,0.32);
    color: #f0d58a; padding: 8px 16px; border-radius: 999px;
    font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
    font-weight: 600; cursor: pointer; transition: all 0.25s ease;
    backdrop-filter: blur(6px);
    margin-right: 10px;
  }
  .hero-badge-btn:hover { background: rgba(201,168,76,0.24); transform: translateY(-1px); }
  .hero-badge-btn.active { background: rgba(201,168,76,0.28); }

  .hero-badge {
    background:rgba(201,168,76,0.15); border:1px solid rgba(201,168,76,0.3);
    color:var(--gold-light); padding:5px 14px; border-radius:20px;
    font-size:11px; letter-spacing:0.1em; text-transform:uppercase;
    margin-bottom:12px; font-weight:500;
  }

  @keyframes fadeFloatUp {
    0%   { opacity: 0; transform: translateY(28px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  .hero-anim { opacity: 0; animation: fadeFloatUp 0.8s cubic-bezier(0.22, 0.9, 0.36, 1) forwards; }
  .hero-anim-d1 { animation-delay: 0.1s; }
  .hero-anim-d2 { animation-delay: 0.28s; }
  .hero-anim-d3 { animation-delay: 0.44s; }
  .hero-anim-d4 { animation-delay: 0.6s; }
  .hero-anim-d5 { animation-delay: 0.76s; }
  .hero-anim-d6 { animation-delay: 0.9s; }

  .hero h1 {
    font-size:clamp(2.2rem, 5vw, 3.4rem); color:var(--white);
    margin-bottom:8px; font-weight:700;
  }
  .hero-sub {
    font-size:clamp(1rem,2vw,1.2rem); color:var(--gold-light); margin-bottom:20px;
    font-family:'Cormorant Garamond',serif; font-weight:400; font-style:italic;
  }
  .hero-desc { font-size:15px; color:#aabbcc; max-width:600px; line-height:1.7; margin-bottom:36px; }
  .hero-stats { display:flex; gap:40px; flex-wrap:wrap; }
  .hero-stat-value {
    font-family:'Cormorant Garamond',serif;
    font-size:2rem; font-weight:700; color:var(--gold);
  }
  .hero-stat-label { font-size:11px; color:#8899aa; text-transform:uppercase; letter-spacing:0.08em; }
  .hero-btns { display:flex; gap:12px; flex-wrap:wrap; }

  .btn-primary {
    background:var(--gold); color:var(--navy);
    border:none; padding:12px 28px; border-radius:4px;
    font-family:'DM Sans',sans-serif; font-size:14px; font-weight:600;
    cursor:pointer; transition:background 0.2s;
  }
  .btn-primary:hover { background:var(--gold-light); }
  .btn-outline {
    background:transparent; color:var(--cream);
    border:1px solid rgba(255,255,255,0.25); padding:12px 28px; border-radius:4px;
    font-family:'DM Sans',sans-serif; font-size:14px; font-weight:500; cursor:pointer;
    transition: border-color 0.2s, color 0.2s;
  }
  .btn-outline:hover { border-color:var(--gold); color:var(--gold); }

  /* ── SECTION TITLE ── */
  .section-title { font-size:clamp(1.6rem,3vw,2.4rem); color:var(--navy); margin-bottom:8px; }
  .section-title.light { color:var(--cream); }
  .section-subtitle { font-size:14px; color:var(--muted); margin-bottom:40px; max-width:640px; }
  .gold-line { width:48px; height:3px; background:linear-gradient(90deg,var(--gold),var(--gold-light)); margin-bottom:16px; border-radius:2px; }

  /* ── SERVICE GRID ── */
  .service-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:20px; align-items: stretch; }
  .service-card {
    background:var(--white); border:1px solid var(--border); border-radius:8px; padding:24px;
    cursor:pointer; transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;
    display: flex; flex-direction: column; height: stretch;
  }
  .service-card:hover { box-shadow:0 8px 32px rgba(10,22,40,0.12); border-color:var(--gold); transform:translateY(-2px); }
  .service-card-icon { font-size:28px; margin-bottom:12px; }
  .service-card-title { font-family:'Cormorant Garamond',serif; font-size:17px; font-weight:600; color:var(--navy); margin-bottom:8px; }
  .service-card-desc { font-size:13px; color:var(--muted); line-height:1.6; }
  .service-card-link { margin-top:14px; font-size:12px; color:var(--gold); font-weight:600; letter-spacing:0.04em; display:flex; align-items:center; gap:4px; }

  /* ── SERVICE DETAIL HEADER
     Replaced old navy gradient with the shared dark gradient at full opacity ── */
  .svc-detail-header {
    background: var(--dark-gradient);
    padding:48px 0 40px; color:var(--cream);
    /* Add same subtle ring as hero for visual continuity */
    position: relative; overflow: hidden;
  }
  .svc-detail-header::before {
    content:''; position:absolute; top:-80px; right:-80px;
    width:380px; height:380px; border-radius:50%;
    border: 1px solid rgba(255,255,255,0.05);
    background: radial-gradient(circle, rgba(255,255,255,0.025) 0%, transparent 70%);
    pointer-events:none;
  }
  .svc-detail-header h2 { font-size:clamp(1.6rem,3vw,2.2rem); color:var(--white); margin-bottom:12px; position:relative; }
  .svc-detail-intro { font-size:14px; color:#aabbcc; max-width:700px; line-height:1.7; position:relative; }

  .svc-items { margin-top:32px; display:flex; flex-direction:column; gap:1px; }
  .svc-item { background:var(--white); border:1px solid var(--border); border-radius:6px; overflow:hidden; transition:box-shadow 0.15s; }
  .svc-item:hover { box-shadow:0 4px 16px rgba(10,22,40,0.08); }
  .svc-item-q { padding:14px 20px; display:flex; align-items:center; justify-content:space-between; cursor:pointer; background:var(--white); }
  .svc-item-q span { font-weight:500; font-size:14px; color:var(--navy); }
  .svc-item-q .dot { width:8px; height:8px; border-radius:50%; background:var(--gold); flex-shrink:0; }
  .svc-item-a { padding:0 20px 14px 20px; font-size:13px; color:var(--muted); line-height:1.65; }
  .back-btn {
    background:none; border:none; cursor:pointer; color:var(--gold); font-size:13px; font-weight:600;
    display:flex; align-items:center; gap:6px; padding:0; margin-bottom:16px;
    font-family:'DM Sans',sans-serif; position:relative;
  }

  /* ── ABOUT ── */
  .about-grid { display:grid; grid-template-columns:1fr 1fr; gap:48px; align-items:start; }
  @media(max-width:720px){ .about-grid { grid-template-columns:1fr; } }
  .corporate-table { width:100%; border-collapse:collapse; font-size:13px; }
  .corporate-table tr { border-bottom:1px solid var(--border); }
  .corporate-table td { padding:10px 8px; }
  .corporate-table td:first-child { color:var(--muted); white-space:nowrap; width:40%; font-weight:500; }
  .corporate-table td:last-child { color:var(--text); font-weight:400; }

  .values-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(240px,1fr)); gap:16px; align-items: stretch; }
  .value-card { background:var(--white); border:1px solid var(--border); border-radius:8px; padding:20px; border-top: 3px solid var(--gold); height: 150px; }
  .value-card h4 { font-family:'Cormorant Garamond',serif; font-size:17px; color:var(--navy); margin-bottom:6px; }
  .value-card p { font-size:13px; color:var(--muted); line-height:1.6; }

  /* ── WHY SWOT SECTION — dark strip uses the shared gradient ── */
  .section-dark {
    padding:64px 0;
    background: var(--dark-gradient);
    position: relative; overflow: hidden;
  }
  .section-dark::before {
    content:''; position:absolute; bottom:-60px; left:-60px;
    width:320px; height:320px; border-radius:50%;
    border: 1px solid rgba(255,255,255,0.04);
    background: radial-gradient(circle, rgba(255,255,255,0.02) 0%, transparent 70%);
    pointer-events:none;
  }

  .why-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(260px,1fr)); gap:20px; align-items: stretch; }
  .why-card {
    background:rgba(255,255,255,0.06); border:1px solid rgba(201,168,76,0.2);
    border-radius:8px; padding:24px; transition:background 0.2s;
    height: 200px;
  }
  .why-card:hover { background:rgba(201,168,76,0.1); }
  .why-card h4 { font-family:'Cormorant Garamond',serif; font-size:18px; color:var(--gold-light); margin-bottom:8px; }
  .why-card p { font-size:13px; color:#aabbcc; line-height:1.65; }

  /* ── INDUSTRIES ── */
  .industry-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:16px; align-items: stretch; }
  .industry-card { background:var(--white); border:1px solid var(--border); border-radius:8px; padding:20px; display:flex; gap:16px; align-items:flex-start; height: stretch; }
  .industry-icon { font-size:28px; flex-shrink:0; }
  .industry-card h4 { font-family:'Cormorant Garamond',serif; font-size:16px; color:var(--navy); margin-bottom:4px; }
  .industry-card p { font-size:12.5px; color:var(--muted); line-height:1.55; }

  /* ── ENGAGEMENT CARDS — dark background uses shared gradient ── */
  .eng-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:20px; align-items: stretch; }
  .eng-card {
    background: var(--dark-gradient);
    border:1px solid rgba(255,255,255,0.08);
    border-radius:8px; padding:28px; color:var(--cream);
    position:relative; overflow:hidden;
    height: 260px;
  }
  .eng-card::before {
    content:''; position:absolute; bottom:-20px; right:-20px;
    width:80px; height:80px; border-radius:50%;
    border:1px solid rgba(201,168,76,0.15);
  }
  .eng-icon { font-size:32px; margin-bottom:16px; }
  .eng-card h3 { font-family:'Cormorant Garamond',serif; font-size:20px; color:var(--gold); margin-bottom:10px; }
  .eng-card p { font-size:13px; color:#aabbcc; line-height:1.65; }

  /* ── QUALITY ── */
  .quality-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:24px; align-items: stretch; }
  .quality-card { background:var(--white); border-radius:8px; padding:28px; border:1px solid var(--border); height: 100%; }
  .quality-card h3 { font-family:'Cormorant Garamond',serif; font-size:20px; color:var(--navy); margin-bottom:16px; padding-bottom:12px; border-bottom:2px solid var(--gold); }
  .quality-card ul { list-style:none; display:flex; flex-direction:column; gap:10px; }
  .quality-card li { font-size:13px; color:var(--muted); line-height:1.6; padding-left:16px; position:relative; }
  .quality-card li::before { content:'▸'; position:absolute; left:0; color:var(--gold); font-size:10px; top:2px; }

  /* ── CONTACT OFFICES — dark cards use shared gradient ── */
  .contact-grid { display:grid; grid-template-columns:1fr 1fr; gap:48px; }
  @media(max-width:720px){ .contact-grid { grid-template-columns:1fr; } }
  .contact-office {
    background: var(--dark-gradient);
    color:var(--cream); border-radius:8px; padding:28px;
    border: 1px solid rgba(255,255,255,0.07);
    position: relative; overflow: hidden;
  }
  .contact-office::after {
    content:''; position:absolute; top:-30px; right:-30px;
    width:140px; height:140px; border-radius:50%;
    border: 1px solid rgba(201,168,76,0.1);
    pointer-events: none;
  }
  .contact-office h3 { font-family:'Cormorant Garamond',serif; font-size:20px; color:var(--gold); margin-bottom:16px; position:relative; }
  .contact-office p { font-size:13.5px; color:#aabbcc; line-height:1.7; position:relative; }
  .contact-office a { color:var(--gold-light); text-decoration:none; }

  .contact-form { display:flex; flex-direction:column; gap:16px; }
  .form-row { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  @media(max-width:500px){ .form-row { grid-template-columns:1fr; } }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field label { font-size:12px; font-weight:600; color:var(--navy); text-transform:uppercase; letter-spacing:0.06em; }
  .form-field input, .form-field select, .form-field textarea {
    border:1px solid var(--border); border-radius:4px; padding:10px 12px;
    font-family:'DM Sans',sans-serif; font-size:14px; background:var(--white);
    color:var(--text); transition: border-color 0.15s; outline:none;
  }
  .form-field input:focus, .form-field select:focus, .form-field textarea:focus { border-color:var(--gold); }
  .form-field textarea { resize:vertical; min-height:90px; }
  .form-disclaimer { font-size:11.5px; color:var(--muted); line-height:1.6; padding:12px; background:#f5f2ec; border-radius:4px; }

  /* ── CAREERS ── */
  .careers-form { max-width:700px; display:flex; flex-direction:column; gap:16px; }

  /* ── INSIGHTS ── */
  .insights-placeholder { background:var(--white); border:1px dashed var(--border); border-radius:8px; padding:48px; text-align:center; color:var(--muted); }
  .insights-placeholder h3 { font-family:'Cormorant Garamond',serif; font-size:22px; color:var(--navy); margin-bottom:8px; }

  /* ── FOOTER — uses shared gradient ── */
  .footer {
    background: var(--dark-gradient);
    color:#8899aa;
    padding:48px 0 24px;
    border-top:1px solid rgba(255,255,255,0.07);
    position: relative; overflow: hidden;
  }
  .footer::before {
    content:''; position:absolute; top:-100px; right:-100px;
    width:400px; height:400px; border-radius:50%;
    border: 1px solid rgba(255,255,255,0.04);
    background: radial-gradient(circle, rgba(255,255,255,0.015) 0%, transparent 70%);
    pointer-events:none;
  }
  .footer-grid { display:grid; grid-template-columns:2fr 1fr 1fr; gap:40px; margin-bottom:40px; position:relative; }
  @media(max-width:720px){ .footer-grid { grid-template-columns:1fr; } }
  .footer-brand h3 { font-family:'Cormorant Garamond',serif; font-size:22px; color:var(--cream); margin-bottom:6px; }
  .footer-brand p { font-size:13px; line-height:1.65; max-width:320px; }
  .footer-brand .tagline { color:var(--gold); font-style:italic; margin-bottom:12px; font-family:'Cormorant Garamond',serif; font-size:15px; }
  .footer-links h4 { color:var(--cream); font-size:13px; font-weight:600; letter-spacing:0.06em; text-transform:uppercase; margin-bottom:14px; }
  .footer-links ul { list-style:none; display:flex; flex-direction:column; gap:8px; }
  .footer-links a { color:#8899aa; text-decoration:none; font-size:13px; transition:color 0.15s; cursor:pointer; }
  .footer-links a:hover { color:var(--gold); }
  .footer-bottom { border-top:1px solid rgba(255,255,255,0.07); padding-top:20px; font-size:12px; display:flex; flex-wrap:wrap; gap:12px; justify-content:space-between; position:relative; }
  .disclaimer-box {
    background: #173253; border-radius:6px;
    font-size:12px; color: var(--cream); line-height:1.65; margin-top:16px;
    border: 1px solid rgba(255,255,255,0.05); position:relative;
    overflow: hidden; padding: 10px 0;  /* removed horizontal padding */
  }
  .disclaimer-box::before,
  .disclaimer-box::after {
    content:''; position:absolute; top:0; bottom:0; width:40px; z-index:2; pointer-events:none;
  }
  .disclaimer-box::before { left:0; background: linear-gradient(to right, #173253, transparent); }
  .disclaimer-box::after  { right:0; background: linear-gradient(to left,  #173253, transparent); }

  .disclaimer-scroll {
    display: inline-flex; white-space: nowrap;
    animation: disclaimer-roll linear infinite;
  }
  .disclaimer-scroll span { padding-right: 80px; } /* gap between repeats */

  @keyframes disclaimer-roll {
    from { transform: translateX(0); }
    to   { transform: translateX(-50%); }
  }

  /* ── MESSAGE BOX — uses shared gradient ── */
  .message-box {
    background: var(--dark-gradient);
    color:var(--cream); border-radius:10px; padding:40px;
    position:relative; overflow:hidden; margin-bottom:32px;
    border: 1px solid rgba(255,255,255,0.07);
  }
  // .message-box::before {
  //   content:'❝'; position:absolute; top:20px; right:28px;
  //   font-size:80px; color:rgba(201,168,76,0.08);
  //   font-family:"Helvetica", Sans-serif; line-height:1;
  // }
  .message-box p { font-size:16px; color:#aabbcc; line-height:1.8; margin-bottom:12px; text-align: justify}
  .message-box p:last-child { margin-bottom:0; }

  /* ── VISION CARDS — use shared gradient ── */
  .vision-card {
    background: var(--dark-gradient);
    border-radius:8px; padding:32px; color:var(--cream);
    border: 1px solid rgba(255,255,255,0.07);
    position: relative; overflow: hidden;
    height: stretch;
  }
  .vision-card::after {
    content:''; position:absolute; bottom:-40px; right:-40px;
    width:160px; height:160px; border-radius:50%;
    border: 1px solid rgba(201,168,76,0.1);
    pointer-events:none;
  }

  /* ── RESPONSIVE ── */
  @media(max-width:600px){
    .container { padding:0 16px; }
    .section { padding:48px 0; }
    .hero { padding:56px 0 48px; }
    .hero-stats { gap:24px; }
    .service-grid { grid-template-columns:1fr; }
  }
`;

// ─── COMPONENT ───────────────────────────────────────────────────────────────

export default function SWOTWeb() {
  const [page, setPage] = useState("home");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedNav, setExpandedNav] = useState({});
  const [formData, setFormData] = useState({});
  const [submitted, setSubmitted] = useState({});

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = GLOBAL_STYLES;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => { window.scrollTo(0,0); }, [page]);

  const navigate = (id) => { setPage(id); setSidebarOpen(false); };
  const toggleExpand = (id) => setExpandedNav(p => ({...p, [id]: !p[id]}));
  const isActivePage = (id) => {
    if (id === page) return true;
    const item = NAV.find(n => n.id === id);
    if (item?.sub) return item.sub.some(s => s.id === page);
    return false;
  };

  // ── SIDEBAR ──
  const Sidebar = () => (
    <>
      <div className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`} onClick={() => setSidebarOpen(false)} />
      <nav className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <div className="header-logo">
            <div><img src={swotLogo} className="header-logo-mark"/></div>
            <div>
              <div className="header-logo-text">SWOT</div>
              <div className="header-logo-sub">Management Solutions</div>
            </div>
          </div>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>✕</button>
        </div>
        <div className="sidebar-scroll">
          {NAV.map(item => (
            <div key={item.id}>
              {item.sub ? (
                <>
                  <button
                    className={`sidebar-nav-item ${isActivePage(item.id) ? "active" : ""} ${expandedNav[item.id] ? "expanded" : ""}`}
                    onClick={() => toggleExpand(item.id)}
                  >
                    {item.label}
                    <span className="arrow">▶</span>
                  </button>
                  <div className="sidebar-sub" style={{ maxHeight: expandedNav[item.id] ? "1000px" : "0" }}>
                    {item.sub.map(s => (
                      <button
                        key={s.id}
                        className={`sidebar-sub-item ${page === s.id ? "active" : ""}`}
                        onClick={() => navigate(s.id)}
                      >{s.label}</button>
                    ))}
                  </div>
                </>
              ) : (
                <button
                  className={`sidebar-nav-item ${isActivePage(item.id) ? "active" : ""}`}
                  onClick={() => navigate(item.id)}
                >{item.label}</button>
              )}
            </div>
          ))}
          <div className="sidebar-divider" />
          <button className="sidebar-nav-item" onClick={() => navigate("contact")} style={{color:"var(--gold)"}}>
            📧 Contact Us
          </button>
        </div>
      </nav>
    </>
  );

  // ── HEADER ──
  const Header = () => (
    <header className="header">
      <button className="sidebar-toggle" onClick={() => setSidebarOpen(true)}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>
      <div className="header-logo" onClick={() => navigate("home")}>
        <div><img src={swotLogo} className="header-logo-mark"/></div>
        <div>
          <div className="header-logo-text">SWOT Management Solutions</div>
          <div className="header-logo-sub">Private Limited · Est. 1997</div>
        </div>
      </div>
      <div className="header-tagline">
        Global Advisory. Trusted Across Borders.
      </div>
      <div className="header-btn">
        <button className="hero-badge-btn active" onClick={() => window.location.assign("https://swotmanagement.co.in")}>
          India Headquartered
        </button>
        <button className="hero-badge-btn" onClick={() => window.location.assign("https://swotsingapore.com")}>
          Singapore APAC
        </button>
      </div>
      <button className="header-contact-btn" onClick={() => navigate("contact")}>Contact Us</button>
    </header>
  );

  // ── FOOTER ──
  const Footer = () => {
    const scrollRef = React.useRef(null);

    React.useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;
      // ~120px per second base, slower on small screens (min 60px/s)
      const pxPerSec = Math.max(20, window.innerWidth * 0.01);
      const duration = (el.scrollWidth / 2) / pxPerSec; // /2 because text is doubled
      el.style.animationDuration = `${duration}s`;
    }, []);

    const text = "The information on this website is for general information only and does not constitute professional advice, legal advice, tax opinion, investment advice, assurance, attestation or guarantee of any outcome. Use of this website does not create a client relationship. SWOT Management Solutions Private Limited does not provide statutory audit, assurance opinions, attest reports or professional certifications reserved for eligible professionals.";

    return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <h3>SWOT Management Solutions Private Limited</h3>
            <div className="tagline">Global Advisory. India Headquartered. Trusted Across Borders.</div>
            <p>CIN: U74140DL1997PTC091154 · Registered Office: New Delhi, India</p>
            <p style={{marginTop:8}}>Email: <a href="mailto:secretarial@swotmanagement.co.in" style={{color:"var(--gold-light)"}}>secretarial@swotmanagement.co.in</a></p>
          </div>
          <div className="footer-links">
            <h4>Company</h4>
            <ul>
              {["about-who","about-vision","about-why","engagement","insights","careers"].map(id => {
                const flat = NAV.flatMap(n => n.sub ? n.sub : [n]);
                const item = flat.find(i => i.id === id);
                return item ? <li key={id}><a onClick={() => navigate(id)}>{item.label}</a></li> : null;
              })}
            </ul>
          </div>
          <div className="footer-links">
            <h4>Services</h4>
            <ul>
              {SERVICES_OVERVIEW.slice(0,7).map(s => (
                <li key={s.id}><a onClick={() => navigate(s.id)}>{s.title.split(" ").slice(0,3).join(" ")}&hellip;</a></li>
              ))}
            </ul>
          </div>
        </div>
        <div className="disclaimer-box">
          <div className="disclaimer-scroll" ref={scrollRef}>
            <span>{text}</span>
            <span>{text}</span> {/* duplicate for seamless loop */}
          </div>
        </div>
        <div className="footer-bottom">
          <span>© 2025 SWOT Management Solutions Private Limited. All rights reserved.</span>
          <span>New Delhi, India · Singapore / APAC</span>
        </div>
      </div>
    </footer>
  )};

  // ── PAGES ──
  const SectionTitle = ({ title, sub, light }) => (
    <div>
      <div className="gold-line" />
      <h2 className={`section-title ${light ? "light" : ""}`}>{title}</h2>
      {sub && <p className="section-subtitle">{sub}</p>}
    </div>
  );

  const HomePage = () => (
    <>
      <div className="hero">
        <div className="hero-bg" style={{ backgroundImage: `url(${indBg})` }}/>
        <div className="hero-overlay" />
        <div className="hero-content">
          <div className="container">
            <h1 className="hero-anim hero-anim-d2">
              SWOT Management Solutions<br />Private Limited
            </h1>
            <div className="hero-sub hero-anim hero-anim-d3">Global Advisory. Trusted Across Borders.</div>
            <p className="hero-desc hero-anim hero-anim-d4">
              An independent professional advisory firm delivering technically rigorous, commercially relevant advisory across taxation, corporate compliance, regulatory, transaction and Virtual CFO mandates — for nearly three decades.
            </p>
            <div className="hero-stats hero-anim hero-anim-d5">
              <div>
                <div className="hero-stat-value">~30</div>
                <div className="hero-stat-label">Years of Practice</div>
              </div>
              <div>
                <div className="hero-stat-value">13+</div>
                <div className="hero-stat-label">Service Areas</div>
              </div>
              <div>
                <div className="hero-stat-value">2</div>
                <div className="hero-stat-label">Jurisdictions</div>
              </div>
            </div>
            <div style={{ marginTop: 36 }} className="hero-btns hero-anim hero-anim-d6">
              <button className="btn-primary" onClick={() => navigate("services")}>Explore Services</button>
              <button className="btn-outline" onClick={() => navigate("contact")}>Get in Touch</button>
            </div>
          </div>
        </div>
      </div>

      {/* Message from Team */}
      <div className="section" style={{background:"var(--white)"}}>
        <div className="container">
          <SectionTitle title="A Message from the Team" sub="Our founding conviction — and the values that guide every engagement." />
          <div className="message-box">
            <p>In an increasingly interconnected global economy, the role of a trusted professional advisor has evolved. Capital crosses borders in days, 
              regulations evolve in months, and the businesses we serve are no longer defined by a single jurisdiction. They operate, raise, invest and trade 
              across multiple geographies — and they require an advisory partner equipped to do the same.</p>
            <p>Since our incorporation in 1997, we have been built on a single, enduring conviction — that disciplined execution, technical depth and unwavering 
              integrity are the foundations on which lasting client relationships are constructed. Over nearly three decades, we have evolved from a domestic 
              professional firm into a globally-oriented advisory house — headquartered in India, anchored by qualified professionals, and increasingly engaged 
              on cross-border mandates across the globe.</p>
            <p>Our work today spans corporate compliance, cross-border tax and structuring, FEMA and RBI advisory, Virtual CFO engagements, transaction support 
              and regulatory representation. We advise inbound investors entering India, Indian businesses expanding internationally, family offices structuring 
              multi-jurisdictional wealth, and multinational groups seeking unified advisory under a single accountable partner.</p>
            <p>The establishment of our Singapore sister concern marks an important milestone in this journey — the first international office of what we intend 
              to develop into a meaningful global advisory practice. From this regional platform we are available to serve clients across Southeast Asia and the 
              broader Asia-Pacific region, while continuing to deepen our India practice.</p>
            <p>We are grateful to our clients, partners and colleagues for the trust they have placed in us. We look forward to creating long-term value, together.</p>

            </div>
        </div>
      </div>

      {/* Services Preview */}
      <div className="section">
        <div className="container">
          <SectionTitle title="Our Service Areas" sub="An integrated advisory platform covering compliance, finance, regulatory, transaction and management needs." />
          <div className="service-grid">
            {SERVICES_OVERVIEW.slice(0,6).map(s => (
              <div className="service-card" key={s.id} onClick={() => navigate(s.id)}>
                <div className="service-card-icon">{s.icon}</div>
                <div className="service-card-title">{s.title}</div>
                <div className="service-card-desc">{s.desc}</div>
                <div className="service-card-link">Learn more →</div>
              </div>
            ))}
          </div>
          <div style={{marginTop:24, textAlign:"center"}}>
            <button className="btn-primary" onClick={() => navigate("services")}>View All Services</button>
          </div>
        </div>
      </div>

      {/* Why SWOT strip — now uses section-dark */}
      <div className="section-dark">
        <div className="container">
          <SectionTitle light title="Why Clients Choose SWOT" sub="" />
          <div className="why-grid">
            {WHY_SWOT.slice(0,4).map((w,i) => (
              <div className="why-card" key={i}>
                <h4>{w.title}</h4>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
          <div style={{marginTop:24}}>
            <button className="btn-outline" onClick={() => navigate("about-why")}>Read More</button>
          </div>
        </div>
      </div>

      {/* Engagement */}
      <div className="section">
        <div className="container">
          <SectionTitle title="Engagement Models" sub="Flexible structures tailored to client needs, scale and stage." />
          <div className="eng-grid">
            {ENGAGEMENT_MODELS.map((e,i) => (
              <div className="eng-card" key={i}>
                <div className="eng-icon">{e.icon}</div>
                <h3>{e.name}</h3>
                <p>{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const AboutWhoPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line"/>
          <h2>A Global Advisory Practice. Headquartered in India.</h2>
          <p className="svc-detail-intro">SWOT Management Solutions Private Limited is an independent professional advisory Company headquartered in New Delhi, India. Incorporated on 15 December 1997 and registered with the Registrar of Companies, Delhi, the firm has built a distinguished track record of nearly three decades in delivering technically rigorous, commercially relevant advisory across taxation, corporate compliance, regulatory, transaction and Virtual CFO mandates.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="about-grid">
            <div>
              <SectionTitle title="Who We Are" />
              <p style={{fontSize:14,color:"var(--muted)",lineHeight:1.8,marginBottom:20}}>
                SWOT is led and supported by a multidisciplinary team with deep functional expertise across finance, taxation, cross border structuring, corporate law support, transaction support, accounting outsourcing, management advisory and regulatory practice. Our leadership brings about thirty years of dedicated experience in cross-border transactions, corporate governance, regulatory representation and complex advisory.
              </p>
              <p style={{fontSize:14,color:"var(--muted)",lineHeight:1.8,marginBottom:20}}>
                Our client portfolio reflects the breadth of our practice — owner-managed businesses, high-growth start-ups, mid-market enterprises, listed corporates, family offices, professional service firms, and the Indian operations of multinational groups. We and our team members have advised on transactions, structuring assignments and regulatory mandates spanning multiple jurisdictions, working with international counsel, banking partners and corporate advisors across the world.
              </p>
              <div style={{marginTop:28}}>
                <SectionTitle title="Who We Serve" />
                <div style={{display:"flex",flexDirection:"column",gap:12,marginTop:8}}>
                  {[
                    ["Domestic Clients","Indian companies, family-managed businesses, family offices and growth enterprises requiring compliance, tax, accounting, regulatory and finance-support services."],
                    ["Multinational Groups","Global organisations seeking India advisory, regulatory support, accounting coordination and integrated business support under a single accountable team."],
                    ["Inbound Clients","International investors and overseas businesses entering India through FDI structures, subsidiaries, LLPs, branch offices, liaison offices, project offices or joint ventures."],
                    ["Outbound Clients","Indian businesses expanding internationally through regional headquarters, holding structures, treasury hubs, IP-holding vehicles, operating subsidiaries and cross-border arrangements."],
                    ["Start-ups & Scaling Businesses","Founder-led, venture-backed or PE-backed businesses requiring finance function support, investor reporting, compliance discipline and management dashboards."],
                  ].map(([title,desc]) => (
                    <div key={title} style={{padding:"12px 16px",background:"var(--white)",border:"1px solid var(--border)",borderRadius:6}}>
                      <div style={{fontWeight:600,fontSize:14,color:"var(--navy)",marginBottom:4}}>{title}</div>
                      <div style={{fontSize:13,color:"var(--muted)"}}>{desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div>
              <SectionTitle title="Corporate Identity" />
              <table className="corporate-table">
                <tbody>
                  {[
                    ["Legal name","SWOT Management Solutions Private Limited"],
                    ["Entity type","Private Company Limited by Shares"],
                    ["Country","India"],
                    ["Date of incorporation","15 December 1997"],
                    ["CIN","U74140DL1997PTC091154"],
                    ["RoC","RoC – Delhi"],
                    ["Registered office","2nd Floor, The Great Eastern Centre, Behind IFCI Tower, Nehru Place, New Delhi – 110019"],
                    ["Primary Email","secretarial@swotmanagement.co.in"],
                    ["International presence","Singapore / Asia-Pacific (APAC) Company"],
                  ].map(([k,v]) => (
                    <tr key={k}><td>{k}</td><td>{v}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const VisionPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line"/>
          <h2>Vision, Mission & Values</h2>
          <p className="svc-detail-intro">Our advisory approach is guided by integrity, technical excellence, client centricity, discretion, accountability and a global outlook.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          {/* Vision / Mission — now using .vision-card */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:32,marginBottom:48}} className="about-grid">
            <div className="vision-card">
              <div className="gold-line"/>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,color:"var(--gold)",marginBottom:16,position:"relative"}}>Our Vision</h3>
              <p style={{fontSize:14,color:"#aabbcc",lineHeight:1.8,position:"relative"}}>To be a globally recognised advisory company anchored by the depth of our India practice, distinguished by the calibre of our professionals and trusted across borders for the quality of our judgment and integrity of our conduct.</p>
            </div>
            <div className="vision-card">
              <div className="gold-line"/>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,color:"var(--gold)",marginBottom:16,position:"relative"}}>Our Mission</h3>
              <p style={{fontSize:14,color:"#aabbcc",lineHeight:1.8,position:"relative"}}>To deliver technically rigorous, commercially relevant and ethically grounded advisory that helps clients navigate complexity, manage risk and pursue growth with confidence — wherever they operate.</p>
            </div>
          </div>
          <SectionTitle title="Core Values" />
          <div className="values-grid">
            {[
              ["Integrity","An uncompromising commitment to ethical conduct, transparency and responsible advisory."],
              ["Technical Excellence","Depth of knowledge, structured review and continuous attention to law, regulation and professional developments."],
              ["Client Centricity","A partnership-led approach that places client priorities, timelines and confidentiality at the centre of delivery."],
              ["Discretion","Respect for client confidentiality, supported by access controls and secure work practices."],
              ["Accountability","Clear ownership, scope clarity, defined deliverables and transparent communication."],
              ["Global Outlook","Multi-jurisdictional fluency and a practical understanding of cross-border business requirements."],
            ].map(([name,desc]) => (
              <div className="value-card" key={name}>
                <h4>{name}</h4>
                <p>{desc}</p>
              </div>
            ))}
          </div>
          <div style={{marginTop:48}}>
            <SectionTitle title="How We Work" />
            <div style={{display:"flex",flexDirection:"column",gap:0}}>
              {[
                "Understand the business context, jurisdictional footprint and commercial objective.",
                "Define the scope, deliverables, responsibilities, timeline and fee basis.",
                "Identify legal, tax, regulatory, accounting and operational dependencies.",
                "Prepare the required analysis, documentation, filings, process notes or deliverables.",
                "Review the work internally before client submission or implementation.",
                "Maintain confidentiality and records in accordance with internal protocols and applicable law.",
              ].map((step,i) => (
                <div key={i} style={{display:"flex",gap:16,padding:"16px 0",borderBottom:"1px solid var(--border)"}}>
                  <div style={{width:32,height:32,borderRadius:"50%",background:"var(--navy)",color:"var(--gold)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:16,flexShrink:0}}>{i+1}</div>
                  <div style={{fontSize:14,color:"var(--muted)",lineHeight:1.7,paddingTop:4}}>{step}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const WhySwotPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line"/>
          <h2>Why SWOT</h2>
          <p className="svc-detail-intro">In a market with no shortage of professional service providers, the choice of advisor matters. The following define what distinguishes SWOT — and why our clients return, engagement after engagement.</p>
        </div>
      </div>
      {/* Full-page dark section for why cards */}
      <div className="section-dark">
        <div className="container">
          <div className="why-grid">
            {WHY_SWOT.map((w,i) => (
              <div className="why-card" key={i}>
                <h4>{w.title}</h4>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const QualityPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line"/>
          <h2>Quality, Integrity & Confidentiality</h2>
          <p className="svc-detail-intro">SWOT's reputation has been built on the trust placed in the company by every client we serve. That trust is protected through a formal framework of confidentiality, integrity and quality control protocols.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="quality-grid">
            <div className="quality-card">
              <h3>Confidentiality</h3>
              <ul>
                <li>Client information is treated as strictly confidential and accessed strictly on a need-to-know basis.</li>
                <li>All professional staff are bound by formal confidentiality undertakings.</li>
                <li>Engagement-specific data rooms, encrypted communications and access-controlled storage are used for sensitive information.</li>
                <li>Client data is retained, archived and destroyed in accordance with applicable law and professional retention requirements.</li>
              </ul>
            </div>
            <div className="quality-card">
              <h3>Quality Control</h3>
              <ul>
                <li>Every deliverable is subject to multi-level review by qualified professionals before issuance.</li>
                <li>Engagement files, working papers and supporting documentation are maintained in accordance with applicable standards.</li>
                <li>Periodic internal quality reviews validate adherence to company-wide methodology and risk-management policies.</li>
                <li>Continuous monitoring of regulatory updates ensures that client advisory remains current and accurate.</li>
              </ul>
            </div>
            <div className="quality-card">
              <h3>Data Protection</h3>
              <ul>
                <li>The Company complies with applicable data protection laws, including the Digital Personal Data Protection Act, 2023 (India).</li>
                <li>Client personal data is processed only for legitimate professional purposes and within applicable legal frameworks.</li>
                <li>Cloud platforms and software tools are selected with reference to security certifications, data residency considerations and contractual data protection commitments.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const ServicesPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line"/>
          <h2>Services</h2>
          <p className="svc-detail-intro">SWOT's service areas are designed to support businesses across recurring compliance, finance-function requirements, cross-border transactions, regulatory filings, business set-up, restructuring and management advisory.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="service-grid">
            {SERVICES_OVERVIEW.map(s => (
              <div className="service-card" key={s.id} onClick={() => navigate(s.id)}>
                <div className="service-card-icon">{s.icon}</div>
                <div className="service-card-title">{s.title}</div>
                <div className="service-card-desc">{s.desc}</div>
                <div className="service-card-link">Explore →</div>
              </div>
            ))}
          </div>
          <div style={{marginTop:32,padding:"20px 24px",background:"#fff8ec",border:"1px solid #e8d5a0",borderRadius:8}}>
            <p style={{fontSize:13,color:"var(--muted)",lineHeight:1.7}}>
              <strong style={{color:"var(--navy)"}}>Note:</strong> SWOT does not provide statutory audit, assurance opinions or attest services. Where a matter requires a licensed professional, registered intermediary or statutory auditor, the work is undertaken only through eligible persons or in coordination with appropriately registered professionals.
            </p>
          </div>
        </div>
      </div>
    </>
  );

  const ServiceDetailPage = ({ id }) => {
    const [openItem, setOpenItem] = useState(null);
    const detail = SERVICE_DETAILS[id];
    if (!detail) return null;
    return (
      <>
        <div className="svc-detail-header">
          <div className="container">
            <button className="back-btn" onClick={() => navigate("services")}>← Back to Services</button>
            <div className="gold-line"/>
            <h2>{detail.title}</h2>
            <p className="svc-detail-intro">{detail.intro}</p>
          </div>
        </div>
        <div className="section">
          <div className="container">
            <div className="svc-items">
              {detail.items.map(([title, desc], i) => (
                <div className="svc-item" key={i}>
                  <div className="svc-item-q" onClick={() => setOpenItem(openItem === i ? null : i)}>
                    <span>{title}</span>
                    <div className="dot" />
                  </div>
                  {openItem === i && <div className="svc-item-a">{desc}</div>}
                </div>
              ))}
            </div>
            <div style={{marginTop:32,textAlign:"center"}}>
              <button className="btn-primary" onClick={() => navigate("contact")}> {id === 'svc-additional' ? "Enquire About These Services" : "Enquire About This Service"} </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  const IndustriesPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line"/>
          <h2>Industries Served</h2>
          <p className="svc-detail-intro">SWOT's advisory and business-support services have been applied across a range of sectors. The following grouping is illustrative and is intended to help visitors understand the company's working familiarity with different business environments.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="industry-grid">
            {INDUSTRIES.map((ind,i) => (
              <div className="industry-card" key={i}>
                <div className="industry-icon">{ind.icon}</div>
                <div>
                  <h4>{ind.group}</h4>
                  <p>{ind.sectors}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const EngagementPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line"/>
          <h2>Engagement Model</h2>
          <p className="svc-detail-intro">SWOT offers flexible engagement structures tailored to client needs, scale and stage. Whether the requirement is a defined-scope advisory mandate, recurring outsourced service or complex multi-jurisdictional transaction.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="eng-grid">
            {ENGAGEMENT_MODELS.map((e,i) => (
              <div className="eng-card" key={i}>
                <div className="eng-icon">{e.icon}</div>
                <h3>{e.name}</h3>
                <p>{e.desc}</p>
              </div>
            ))}
          </div>
          <div style={{marginTop:32,padding:"24px",background:"var(--white)",border:"1px solid var(--border)",borderRadius:8}}>
            <p style={{fontSize:13.5,color:"var(--muted)",lineHeight:1.8}}>
              Detailed engagement letters or service orders are issued for every assignment after scope finalisation, independence/conflict evaluation where applicable, fee agreement and identification of any regulated services requiring eligible professionals.
            </p>
          </div>
          <div style={{marginTop:24,textAlign:"center"}}>
            <button className="btn-primary" onClick={() => navigate("contact")}>Start a Conversation</button>
          </div>
        </div>
      </div>
    </>
  );

  const InsightsPage = () => (
    <>
      <div className="svc-detail-header">
        <div className="container">
          <div className="gold-line"/>
          <h2>Insights & Professional Updates</h2>
          <p className="svc-detail-intro">SWOT publishes professional updates, articles, briefers and resource notes to assist readers in keeping track of relevant professional and regulatory developments.</p>
        </div>
      </div>
      <div className="section">
        <div className="container">
          <div className="insights-placeholder">
            <h3>Coming Soon</h3>
            <p style={{fontSize:14,marginBottom:8}}>Professional updates on Tax, GST, FEMA, RBI, Corporate Compliance, Transactions, Accounting, Virtual CFO and Regulatory matters will be published here.</p>
            <p style={{fontSize:12,marginTop:16,color:"#9aabb5"}}>The materials in this section are for general informational purposes only and do not constitute professional advice.</p>
          </div>
        </div>
      </div>
    </>
  );

  const CareersPage = () => {
    const [sent, setSent] = useState(false);
    return (
      <>
        <div className="svc-detail-header">
          <div className="container">
            <div className="gold-line"/>
            <h2>Careers</h2>
            <p className="svc-detail-intro">SWOT offers professional opportunities for individuals interested in tax, regulatory advisory, corporate compliance, accounting, Virtual CFO support, transaction advisory, payroll, research and business-support services.</p>
          </div>
        </div>
        <div className="section">
          <div className="container">
            {sent ? (
              <div style={{padding:40,textAlign:"center",background:"var(--white)",borderRadius:8,border:"1px solid var(--border)"}}>
                <div style={{fontSize:40,marginBottom:16}}>✅</div>
                <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,color:"var(--navy)",marginBottom:8}}>Application Received</h3>
                <p style={{color:"var(--muted)",fontSize:14}}>Thank you for your interest in SWOT. We will review your application and respond accordingly.</p>
              </div>
            ) : (
              <div className="careers-form">
                <SectionTitle title="Apply to SWOT" sub="Candidates interested in applying for professional or support roles may complete the form below." />
                <div className="form-row">
                  <div className="form-field"><label>Full Name *</label><input type="text" placeholder="Your full name" /></div>
                  <div className="form-field"><label>Email *</label><input type="email" placeholder="your@email.com" /></div>
                </div>
                <div className="form-row">
                  <div className="form-field"><label>Phone</label><input type="tel" placeholder="+91 or +65 number" /></div>
                  <div className="form-field"><label>Qualification</label><input type="text" placeholder="CA / MBA / LLB etc." /></div>
                </div>
                <div className="form-row">
                  <div className="form-field"><label>Position Applied For</label><input type="text" placeholder="e.g. Tax Associate" /></div>
                  <div className="form-field">
                    <label>Experience</label>
                    <select>
                      <option value="">Select experience</option>
                      <option>Fresher / Trainee</option>
                      <option>1–3 Years</option>
                      <option>3–6 Years</option>
                      <option>6–10 Years</option>
                      <option>10+ Years</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label>Area of Interest</label>
                    <select>
                      <option value="">Select area</option>
                      <option>Cross-Border Tax & Structuring</option>
                      <option>FEMA / RBI / Regulatory</option>
                      <option>Corporate Compliance</option>
                      <option>Virtual CFO / Accounting</option>
                      <option>Direct & Indirect Tax</option>
                      <option>Transaction & M&A</option>
                      <option>Payroll & HR</option>
                      <option>Business Support</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Preferred City / Arrangement</label>
                    <input type="text" placeholder="New Delhi / Remote etc." />
                  </div>
                </div>
                <div className="form-field">
                  <label>Cover Note</label>
                  <textarea placeholder="Brief note about your interest and background..." />
                </div>
                <div className="form-disclaimer">
                  Personal information submitted will be used only for processing your application. Submission does not guarantee an interview or offer. SWOT Management Solutions Private Limited complies with the Digital Personal Data Protection Act, 2023.
                </div>
                <div>
                  <button className="btn-primary" onClick={() => setSent(true)}>Submit Application</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const ContactPage = () => {
    const [sent, setSent] = useState(false);
    return (
      <>
        <div className="svc-detail-header">
          <div className="container">
            <div className="gold-line"/>
            <h2>Contact Us</h2>
            <p className="svc-detail-intro">For professional and business enquiries, please write to SWOT Management Solutions Private Limited. The relevant team member will respond based on the nature of the enquiry.</p>
          </div>
        </div>
        <div className="section">
          <div className="container">
            <div className="contact-grid">
              <div>
                <div className="contact-office" style={{marginBottom:20}}>
                  <h3>India — Global Headquarters</h3>
                  <p>SWOT Management Solutions Private Limited<br/>
                  2nd Floor, The Great Eastern Centre<br/>
                  Behind IFCI Tower, Nehru Place<br/>
                  New Delhi – 110019, India</p>
                  <p style={{marginTop:12}}>Email: <a href="mailto:secretarial@swotmanagement.co.in">secretarial@swotmanagement.co.in</a></p>
                  <p style={{ marginTop: 8 }}>
                    <a href="https://www.linkedin.com/company/swot-management-solution-private-limited/" target="_blank" rel="noopener noreferrer">LinkedIn →</a>
                  </p>
                </div>
                <div className="contact-office" style={{ marginBottom: 20 }}>
                  <h3>Singapore Office</h3>
                  <p>SWOT Singapore Private Limited<br />
                    20 CECIL STREET, #05-03, PLUS, SINGAPORE 049705</p>
                  <p style={{ marginTop: 12 }}>Email: <a href="mailto:secretarial@swotsingapore.com">secretarial@swotsingapore.com</a></p>
                  <p style={{ marginTop: 8, color: "#8899aa", fontSize: 12.5 }}>
                    Phone: to be updated.
                  </p>
                  <p style={{ marginTop: 8 }}>
                    <a href="https://www.linkedin.com/company/swot-singapore-pte-ltd" target="_blank" rel="noopener noreferrer">LinkedIn →</a>
                  </p>
                </div>
              </div>
              <div>
                {sent ? (
                  <div style={{padding:40,textAlign:"center",background:"var(--white)",borderRadius:8,border:"1px solid var(--border)"}}>
                    <div style={{fontSize:40,marginBottom:16}}>✅</div>
                    <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,color:"var(--navy)",marginBottom:8}}>Enquiry Received</h3>
                    <p style={{color:"var(--muted)",fontSize:14}}>Thank you for reaching out. We will review your enquiry and respond subject to scope evaluation and resource availability.</p>
                  </div>
                ) : (
                  <div className="contact-form">
                    <SectionTitle title="Send an Enquiry" />
                    <div className="form-row">
                      <div className="form-field"><label>Name *</label><input type="text" placeholder="Full name" /></div>
                      <div className="form-field"><label>Company</label><input type="text" placeholder="Company / organisation" /></div>
                    </div>
                    <div className="form-row">
                      <div className="form-field"><label>Email *</label><input type="email" placeholder="your@email.com" /></div>
                      <div className="form-field"><label>Phone</label><input type="tel" placeholder="+91 / +65" /></div>
                    </div>
                    <div className="form-row">
                      <div className="form-field"><label>Country / Jurisdiction</label><input type="text" placeholder="e.g. India, Singapore" /></div>
                      <div className="form-field">
                        <label>Service Area of Interest</label>
                        <select>
                          <option value="">Select service area</option>
                          {SERVICES_OVERVIEW.map(s => <option key={s.id}>{s.title}</option>)}
                          <option>General Enquiry</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-field">
                      <label>Brief Enquiry *</label>
                      <textarea placeholder="Please describe your requirement briefly..." />
                    </div>
                    <div className="form-disclaimer">
                      Submission of an enquiry does not create a client relationship. SWOT will review the enquiry and respond subject to scope evaluation, conflict checks where applicable, resource availability and applicable legal or regulatory requirements. Personal information submitted will be used only for responding to this enquiry.
                    </div>
                    <div>
                      <button className="btn-primary" onClick={() => setSent(true)}>Send Enquiry</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  // ── RENDER PAGE ──
  const renderPage = () => {
    if (page === "home") return <HomePage />;
    if (page === "about-who") return <AboutWhoPage />;
    if (page === "about-vision") return <VisionPage />;
    if (page === "about-why") return <WhySwotPage />;
    if (page === "about-quality") return <QualityPage />;
    if (page === "services") return <ServicesPage />;
    if (SERVICE_DETAILS[page]) return <ServiceDetailPage id={page} />;
    if (page === "industries") return <IndustriesPage />;
    if (page === "engagement") return <EngagementPage />;
    if (page === "insights") return <InsightsPage />;
    if (page === "careers") return <CareersPage />;
    if (page === "contact") return <ContactPage />;
    return <HomePage />;
  };

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="page-wrap">
        {renderPage()}
        <Footer />
      </div>
    </div>
  );
}
